import { useMutation, useQuery, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import type { ContactListItem, CreateContactInput, Paginated, UpdateContactInput } from '@crm/shared';
import { api } from '../../api/client';
import type { ListParams } from '../../components/list-params';

export interface ContactDetail extends ContactListItem {
  counts: { deals: number; tasks: number; threads: number; openTasks?: number };
  lists: { id: string; name: string }[];
  segments: { id: string; name: string }[];
  mergedIntoId?: string | null;
}

export const contactName = (c: { firstName?: string | null; lastName?: string | null; email?: string | null }) =>
  [c.firstName, c.lastName].filter(Boolean).join(' ') || c.email || '(no name)';

export function useContacts(params: ListParams, extra: Record<string, string> = {}) {
  return useQuery({
    queryKey: ['contacts', params, extra],
    queryFn: async () => (await api.get<Paginated<ContactListItem>>('/contacts', { params: { ...params, ...extra } })).data,
    placeholderData: keepPreviousData,
  });
}

export function useContact(id: string | undefined) {
  return useQuery({
    queryKey: ['contact', id],
    queryFn: async () => (await api.get<ContactDetail>(`/contacts/${id}`)).data,
    enabled: !!id,
  });
}

function useInvalidateContacts() {
  const qc = useQueryClient();
  return (id?: string) => {
    void qc.invalidateQueries({ queryKey: ['contacts'] });
    void qc.invalidateQueries({ queryKey: ['dashboard'] });
    if (id) {
      void qc.invalidateQueries({ queryKey: ['contact', id] });
      void qc.invalidateQueries({ queryKey: ['activities'] });
    }
  };
}

export function useCreateContact() {
  const inv = useInvalidateContacts();
  return useMutation({
    mutationFn: async (input: CreateContactInput) => (await api.post<ContactListItem>('/contacts', input)).data,
    onSuccess: () => inv(),
  });
}

export function useUpdateContact() {
  const inv = useInvalidateContacts();
  return useMutation({
    mutationFn: async ({ id, ...input }: UpdateContactInput & { id: string }) =>
      (await api.patch<ContactListItem>(`/contacts/${id}`, input)).data,
    onSuccess: (_d, v) => inv(v.id),
  });
}

export function useDeleteContacts() {
  const inv = useInvalidateContacts();
  return useMutation({
    mutationFn: async (ids: string[]) => (await api.post<{ deleted: number }>('/contacts/bulk-delete', { ids })).data,
    onSuccess: () => inv(),
  });
}
